import { ReactNode } from "react";
import DashboardIcon from "@mui/icons-material/GridViewOutlined";
import PeopleIcon from "@mui/icons-material/PeopleAltOutlined";
import EmailIcon from "@mui/icons-material/MailOutlined";
import SettingsIcon from "@mui/icons-material/SettingsOutlined";
import FolderIcon from "@mui/icons-material/FolderOutlined";
import AssignmentIcon from "@mui/icons-material/AssignmentOutlined";
import GavelIcon from "@mui/icons-material/GavelOutlined";
import ReceiptIcon from "@mui/icons-material/ReceiptLongOutlined";
import SavingsIcon from "@mui/icons-material/SavingsOutlined";
import HomeWorkIcon from "@mui/icons-material/HomeWorkOutlined";
import TaskIcon from "@mui/icons-material/FactCheckOutlined";
import HandshakeIcon from "@mui/icons-material/HandshakeOutlined";
import WalletIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import PriceIcon from "@mui/icons-material/SellOutlined";

export interface NavItem {
  /** Display label. */
  title: string;
  /** Route this item navigates to. */
  path: string;
  icon?: ReactNode;
  /** Two-to-three letter marker shown in place of an icon for case types. */
  abbr?: string;
  /**
   * Match strategy. "exact" highlights only on an identical path,
   * "prefix" also highlights on descendants.
   */
  match?: "exact" | "prefix";
  /** Extra terms the command palette should match on. */
  keywords?: string[];
}

export interface NavSection {
  /** Uppercase label above the group; omitted for the leading group. */
  title?: string;
  items: NavItem[];
}

export const NAV_SECTIONS: NavSection[] = [
  {
    title: "Übersicht",
    items: [
      {
        title: "Dashboard",
        path: "/",
        icon: <DashboardIcon fontSize="small" />,
        match: "exact",
        keywords: ["home", "start", "übersicht"],
      },
      {
        title: "Kunden",
        path: "/users",
        icon: <PeopleIcon fontSize="small" />,
        match: "prefix",
        keywords: ["nutzer", "user", "mandanten"],
      },
      {
        title: "Newsletter",
        path: "/newsletter",
        icon: <EmailIcon fontSize="small" />,
        match: "prefix",
        keywords: ["mail", "versand"],
      },
    ],
  },
  {
    title: "Fälle",
    items: [
      {
        title: "Mietminderung",
        abbr: "MM",
        path: "/mietminderung",
        icon: <GavelIcon fontSize="small" />,
        match: "prefix",
        keywords: ["mangel", "minderung"],
      },
      {
        title: "Mietendeckel",
        abbr: "MD",
        path: "/mietendeckel",
        icon: <HomeWorkIcon fontSize="small" />,
        match: "prefix",
        keywords: ["deckel"],
      },
      {
        title: "Mietpreisbremse",
        abbr: "MPB",
        path: "/mietpreisbremse",
        icon: <AssignmentIcon fontSize="small" />,
        match: "prefix",
        keywords: ["bremse", "preis"],
      },
      {
        title: "Mietrechtsanfragen",
        abbr: "MRA",
        path: "/mietrechtsanfragen",
        icon: <FolderIcon fontSize="small" />,
        match: "prefix",
        keywords: ["anfrage", "general", "tenant"],
      },
      {
        title: "Nebenkosten",
        abbr: "NK",
        path: "/nebenkosten",
        icon: <ReceiptIcon fontSize="small" />,
        match: "prefix",
        keywords: ["betriebskosten", "abrechnung", "operating"],
      },
      {
        title: "Kaution",
        abbr: "KA",
        path: "/kaution",
        icon: <SavingsIcon fontSize="small" />,
        match: "prefix",
        keywords: ["deposit", "sicherheit"],
      },
    ],
  },
  {
    title: "Verwaltung",
    // Flat rather than nested under Einstellungen: each settings screen
    // is its own destination, reachable in one click.
    items: [
      {
        title: "Einstellungen",
        path: "/settings",
        icon: <SettingsIcon fontSize="small" />,
        match: "exact",
        keywords: ["konfiguration", "settings", "allgemein"],
      },
      {
        title: "Aufgabentypen",
        path: "/settings/tasks",
        icon: <TaskIcon fontSize="small" />,
        match: "exact",
        keywords: ["tasks", "aufgaben"],
      },
      {
        title: "Partner",
        path: "/settings/partners",
        icon: <HandshakeIcon fontSize="small" />,
        match: "exact",
      },
      {
        title: "Finanzen",
        path: "/settings/finances",
        icon: <WalletIcon fontSize="small" />,
        match: "exact",
        keywords: ["finance", "umsatz"],
      },
      {
        title: "Preise",
        path: "/settings/prices",
        icon: <PriceIcon fontSize="small" />,
        match: "exact",
        keywords: ["prices", "tarife"],
      },
    ],
  },
];

/** True when `pathname` should light up `item`. */
export const isItemActive = (item: NavItem, pathname: string): boolean => {
  if (item.match === "exact") return pathname === item.path;
  if (item.path === "/") return pathname === "/";
  return pathname === item.path || pathname.startsWith(`${item.path}/`);
};

/** Every destination as one flat list, for search and titling. */
export const flattenNav = (): NavItem[] =>
  NAV_SECTIONS.flatMap((section) => section.items);

/**
 * Title for the current route, used by the top bar.
 * Falls back to the app name so the bar is never empty.
 */
export const getPageTitle = (pathname: string): string => {
  const candidates = flattenNav().filter((item) =>
    item.path === "/"
      ? pathname === "/"
      : pathname === item.path || pathname.startsWith(`${item.path}/`),
  );

  if (!candidates.length) return "CaseHero";

  // Deepest match wins, so /settings/prices beats /settings.
  return candidates.reduce((best, item) =>
    item.path.length > best.path.length ? item : best,
  ).title;
};
